<template>
    <BasePopover
        as-child
        side="bottom"
        align="end"
        :side-offset="16"
        :collision-padding="16"
        content-class="w-96 max-w-[calc(100vw-2rem)] border-[#E7E2DB] bg-[#FDFDFB] p-5"
        :open="open"
        @update:open="updateOpen">
        <template #trigger>
            <slot name="trigger" />
        </template>
        <template #content>
            <form data-testid="development-journeys-feedback-dialog" class="flex flex-col gap-6" @submit.prevent="submit">
                <h2 class="text-lg leading-6 font-normal text-[#524D46] font-sans">What’s working, what isn’t?</h2>
                <BaseTextarea
                    id="development-journeys-feedback-comment"
                    v-model:input="comment"
                    data-testid="development-journeys-feedback-comment"
                    aria-label="Your feedback"
                    placeholder="Your feedback"
                    :maxlength="FEEDBACK_TEXT_MAX_LENGTH"
                    required
                    :disabled="isSubmitting"
                    disable-autosize
                    show-resize-handle
                    class="min-h-32 resize-y rounded-lg border border-[#C4BDB2] bg-white p-3 text-valence-grey-850 placeholder:text-[#A29B90] transition-colors focus-visible:border-valence-primary-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-valence-primary-500/20" />

                <p role="alert" aria-atomic="true" class="sr-only">{{ errorMessage }}</p>
                <p v-if="errorMessage" class="body-small text-red-700">{{ errorMessage }}</p>

                <div class="flex justify-end gap-3">
                    <BaseButton type="button" theme="neutral" class="!border-0 !bg-transparent !px-3 !shadow-none hover:!bg-[#F7F5F2]" :disabled="isSubmitting" @click="close">
                        Cancel
                    </BaseButton>
                    <BaseButton type="submit" theme="primary" class="base-btn-dark" :disabled="!canSubmit" :aria-busy="isSubmitting">
                        {{ isSubmitting ? "Sending…" : "Send" }}
                    </BaseButton>
                </div>
            </form>
        </template>
    </BasePopover>
</template>

<script setup lang="ts">
import type { DevelopmentJourneysFeedbackPayload } from "~vue/api/generated/feedback";
import BaseButton from "~vue/components/BaseButton.vue";
import BasePopover from "~vue/components/BasePopover.vue";
import BaseTextarea from "~vue/components/form/BaseTextarea.vue";
import { computed, ref, watch } from "vue";

const FEEDBACK_TEXT_MAX_LENGTH = 2_000;

const props = defineProps<{
    isSubmitting: boolean;
    errorMessage: string | null;
}>();
const emit = defineEmits<{
    submit: [payload: DevelopmentJourneysFeedbackPayload];
}>();
const open = defineModel<boolean>("open", { default: false });
const comment = ref("");
const canSubmit = computed(() => !props.isSubmitting && comment.value.trim().length > 0);

const close = () => {
    if (!props.isSubmitting) {
        open.value = false;
    }
};

const updateOpen = (isOpen: boolean) => {
    if (props.isSubmitting && !isOpen) {
        return;
    }
    open.value = isOpen;
};

const submit = () => {
    if (!canSubmit.value) {
        return;
    }
    emit("submit", { comment: comment.value.trim() });
};

watch(open, (isOpen) => {
    if (!isOpen) {
        comment.value = "";
    }
});
</script>
